import React, { FC, useState, useEffect } from 'react'
import { jsx } from '@emotion/core'
import {
  useDisclosure,
  Modal,
  ModalContent,
  ModalBody,
  Icon,
  Button,
  Box,
  Image,
  Heading,
  Text,
  ModalCloseButton,
} from '@chakra-ui/core'
import { RemoveScroll } from 'react-remove-scroll'
import { isIOS, isMobileOnly } from 'react-device-detect'
import { useRecoilValue, useRecoilState } from 'recoil'
import { configState, cartState, orderConfigState, orderState } from '../../recoil/atoms'
import { cartStatsState } from '../../recoil/selectors'
import { CartItem } from '../../components/CartItem'
import { LinkButton } from '../../lib/types'
import { Button as ButtonComponent, AdditionalInfo } from '@engyalo/storefront-components'
import { css } from '@emotion/core'
import {
  orderSent,
  removeProductFromCartCanceled,
  removeProductFromCartCompleted,
  resetCartCanceled,
  resetCartCompleted,
  resetCartStart,
  viewCart,
} from '@engyalo/storefront-events'
import { useMutation, useQuery } from '@apollo/client'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { createOrderMutation, customCart } from '../../queries'
import { getStoreFront_orderConfig as IOrderConfig } from '@engyalo/storefront-types/lib/graphql/storefront'
import {
  createOrderVariables,
  createOrder_createOrder as ICreateOrder,
} from '@engyalo/storefront-types/lib/graphql/order'
import { IProduct, IConfig, IOrder } from '@engyalo/storefront-types/lib/plugins/model'
import { customCartVariables, customCart as ICustomCart } from '@engyalo/storefront-types/lib/graphql/cart'
import { OrderStatus } from '@engyalo/storefront-types/lib/graphql/global-types'
import { ExecutionResult } from 'graphql'
import { MinAmountProgress } from '../../components/MinAmountProgress'
import { getFlatProductValues } from '../../utils/analytics'
import { ILocationSearch, isInPreviewMode, skipIfInPreviewMode } from '../../utils/mode'
import { usePlugin } from '../../Plugin'
import { Hook, IStorefrontSettings } from '@engyalo/storefront-types'

export const Cart: FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation<ILocationSearch>()
  const { customer, order } = useParams()
  const config = useRecoilValue<IConfig>(configState)
  const orderConfig = useRecoilValue<IOrderConfig>(orderConfigState)
  const [cart, setCart] = useRecoilState(cartState)
  const { totalNum, totalPrice } = useRecoilValue(cartStatsState)
  const [isCartVerified, setCartIsVerified] = React.useState(false)
  const [productToDelete, setProductToDelete] = useState<IProduct | null>(null)
  const [globalOrder, setGlobalOrder] = useRecoilState<IOrder>(orderState)
  const storefrontPlugin = usePlugin(Hook.STOREFRONT)
  const storefrontSettings = storefrontPlugin?.props as IStorefrontSettings
  const hidePrices = storefrontSettings?.hidePrices ?? false
  const [verticalHeight, setVerticalHeight] = useState(window.innerHeight * 0.01)

  const { isOpen, onOpen, onClose } = useDisclosure()

  const session = {
    userID: orderConfig.userID || '',
    customer: config.customer,
  }

  useQuery<ICustomCart, customCartVariables>(customCart, {
    variables: {
      storefrontID: config.storefrontId,
      orderConfigID: orderConfig.id || '',
      /**
       * This is ugly but needed by now. Product query gets an
       * autogenerated `__typename` property which is useful for caching
       * but `customCart` query doesn't expect it, thus we need to remove
       * it from the cart items list, however TS complains because it
       * doesn't find a `__typename` property in the IProduct interface :(
       * TODO: Remove @ts-ignore by ensuring the right types in all places
       */
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      cart: Object.values(cart).map(({ __typename, ...product }) => product),
    },
    skip: !config.enabledCustomCart || isCartVerified,
    onCompleted(data: ICustomCart) {
      const verifiedCart = data.customCart.reduce(
        (prev, cartItem) => ({
          ...prev,
          [cartItem?.id || '']: cartItem,
        }),
        {}
      )
      setCartIsVerified(true)
      setCart(verifiedCart)
    },
  })

  const deleteCart = (): void => {
    if (productToDelete) {
      skipIfInPreviewMode(() => {
        removeProductFromCartCompleted({
          product: {
            id: productToDelete.id || '',
            sku: productToDelete.sku || '',
            name: productToDelete.name || '',
            price: productToDelete.price || 0,
            totalPrice:
              productToDelete?.price && productToDelete?.quantity
                ? productToDelete.price * productToDelete.quantity
                : 0,
            category: productToDelete.category || '',
          },
          session,
        })
      })
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      deleteAction(productToDelete)
    } else {
      skipIfInPreviewMode(() => {
        const getCartItemValues = getFlatProductValues(cart)

        resetCartCompleted({
          cart: {
            size: totalNum,
            value: Number(totalPrice),
            ids: getCartItemValues('id'),
            skus: getCartItemValues('sku'),
            names: getCartItemValues('name'),
            categories: getCartItemValues('category'),
            note: globalOrder.orderNote,
          },
          session,
        })
      })
      setCart({})
    }
    onClose()
  }
  const onTextChange = (e: React.FormEvent<HTMLInputElement>): void => {
    setGlobalOrder({
      ...globalOrder,
      orderNote: e.currentTarget.value,
    })
  }

  const [createOrder] = useMutation<ICreateOrder, createOrderVariables>(createOrderMutation, {
    variables: {
      order: {
        storefrontID: config.storefrontId,
        userID: orderConfig.userID || '',
        status: OrderStatus.ENABLED,
        provider: orderConfig.channelName,
        total: parseFloat(totalPrice),
        totalQty: totalNum,
        cart: Object.values(cart),
        note: globalOrder.orderNote,
      },
      configId: order,
    },
    onCompleted: () => {
      if (storefrontSettings?.onOrderCreated) {
        storefrontSettings.onOrderCreated({ cart, orderConfig, t })
        return
      }
      if (orderConfig.channelName === 'whatsapp') {
        window.location.href = `http://wa.me/${orderConfig.channelID}`
      }
    },
  })

  useEffect(() => {
    skipIfInPreviewMode(() => {
      const getCartItemValues = getFlatProductValues(cart)
      viewCart({
        cart: {
          size: totalNum,
          value: Number(totalPrice),
          ids: getCartItemValues('id'),
          skus: getCartItemValues('sku'),
          names: getCartItemValues('name'),
          categories: getCartItemValues('category'),
          note: globalOrder.orderNote,
        },
        session,
      })
    })
  }, [])

  const deleteItem = (product: IProduct): void => {
    setProductToDelete(product)
    onOpen()
  }

  const deleteAction = (product: IProduct): void => {
    const tempCart = { ...cart }
    if (product && product.cartId) {
      const currentSku = product.cartId
      if (tempCart[currentSku]) {
        delete tempCart[currentSku]
      }
    }
    setCart(tempCart)
    setProductToDelete(null)
  }

  const getListHeight = (): string => {
    const headerHeight = 91
    const footerHeightOrderNote = 196
    const footerHeightNoOrderNote = 95
    const footerReadyToOrder = 64
    const footerReadyToOrderAndOrderNote = 165

    const innerHeight = window.innerHeight
    if (isIOS && isMobileOnly) {
      if (config.enabledOrderNote) {
        if (canPlaceOrder) {
          return `${innerHeight - headerHeight - footerReadyToOrderAndOrderNote}px`
        }

        return `${innerHeight - headerHeight - footerHeightOrderNote}px`
      }

      if (canPlaceOrder) {
        return `${innerHeight - headerHeight - footerReadyToOrder}px`
      }

      return `${innerHeight - headerHeight - footerHeightNoOrderNote}px`
    }

    return '100%'
  }

  const canPlaceOrder = !config.minAmount || Number(totalPrice) >= config.minAmount

  useEffect(() => {
    function handleResize() {
      const newVerticalHeight = window.innerHeight * 0.01
      setVerticalHeight(newVerticalHeight)
    }

    window.addEventListener('resize', handleResize)
  })

  const getMinHeight = () => {
    const screenHeight = window.innerHeight
    const headerHeight = 78
    const footerButton = 17 + 24
    const footerMinOrder = 23
    const footerOrderNote = 102
    const footerPadding = 24

    console.log(screenHeight)
    if (config) {
      if (isMobileOnly) {
        // Products selected
        if (totalNum > 0) {
          if (config.enabledOrderNote) {
            if (config && config.minAmount && config.minAmount > 0) {
              const minHeight =
                screenHeight - footerPadding - footerButton - footerMinOrder - footerOrderNote - headerHeight
              console.log(minHeight)
              return minHeight
            } else {
              const minHeight = screenHeight - footerPadding - footerButton - footerOrderNote - headerHeight
              console.log(minHeight)
              return minHeight
            }
          } else {
            if (config && config.minAmount && config.minAmount > 0) {
              const minHeight = screenHeight - footerPadding - footerButton - footerMinOrder - headerHeight
              console.log(minHeight)
              return minHeight
            } else {
              const minHeight = screenHeight - footerPadding - footerButton - headerHeight
              console.log(minHeight)
              return minHeight
            }
          }
        } else {
          if (config && config.minAmount && config.minAmount > 0) {
            const minHeight = screenHeight - footerPadding - footerButton - footerMinOrder - headerHeight
            console.log(minHeight)
            return minHeight
          } else {
            const minHeight = screenHeight - footerPadding - footerButton - headerHeight
            console.log(minHeight)
            return minHeight
          }
        }
      }
    }

    return ''
  }
  getMinHeight()

  return (
    <Box h={isMobileOnly ? `calc(var(--vh, 1${verticalHeight}) * 100)` : '100%'} w="100%" d="flex" flexDir="column">
      {isOpen && (
        <Box position="absolute" opacity={0.5} backgroundColor="black" width="100%" height="100%" zIndex={50} />
      )}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalContent>
          <RemoveScroll>
            <ModalCloseButton
              background="transparent"
              borderStyle="none"
              borderColor="unset"
              onClick={(event: React.MouseEvent<HTMLButtonElement>): void => {
                event.preventDefault()
                if (productToDelete) {
                  skipIfInPreviewMode(() => {
                    removeProductFromCartCanceled({
                      product: {
                        id: productToDelete.id || '',
                        sku: productToDelete.sku || '',
                        name: productToDelete.name || '',
                        price: productToDelete.price || 0,
                        totalPrice:
                          productToDelete.price && productToDelete.quantity
                            ? productToDelete.price * productToDelete.quantity
                            : 0,
                        category: productToDelete.category || '',
                      },
                      session,
                    })
                  })
                } else {
                  const getCartItemValues = getFlatProductValues(cart)
                  skipIfInPreviewMode(() => {
                    resetCartCanceled({
                      cart: {
                        size: totalNum,
                        value: Number(totalPrice),
                        ids: getCartItemValues('id'),
                        skus: getCartItemValues('sku'),
                        names: getCartItemValues('name'),
                        categories: getCartItemValues('category'),
                        note: globalOrder.orderNote,
                      },
                      session,
                    })
                  })
                }

                onClose()
              }}
            />
            <ModalBody>
              <Heading as="h3" fontSize="md" textAlign="center" paddingTop="4rem" paddingBottom="1.6rem">
                {productToDelete
                  ? t('cart-removeQuestion', { product: productToDelete.name })
                  : t('cart-removeAllQuestion')}
                <span role="img" aria-label="scream">
                  😱
                </span>
              </Heading>
              <Box paddingBottom="2rem">
                <ButtonComponent onClick={deleteCart} danger>
                  {productToDelete ? t('cart-remove') : t('cart-removeAll')}
                </ButtonComponent>
              </Box>
            </ModalBody>
          </RemoveScroll>
        </ModalContent>
      </Modal>
      <Box
        position="sticky"
        background="white"
        flex="0 0 auto"
        d="flex"
        top={0}
        zIndex={2}
        width="100%"
        maxW="42.5rem"
        flexDir="column"
      >
        <Box h="4rem" d="flex" width="100%" maxW="42.5rem">
          <LinkButton
            to={{ pathname: `/${customer}/${order}`, search: location.search }}
            h="100%"
            w="3.2rem"
            background="transparent"
            borderColor="transparent"
            size="lg"
            aria-label="cart"
            icon="chevron-left"
          />
          <Image alt="logo" h="3.6rem" alignSelf="center" margin="auto" src={config.logo} />
        </Box>
        <Box
          padding="1.2rem 1.2rem 0.8rem 1.6rem"
          borderBottom="0.1rem solid"
          borderColor="gray.200"
          position="sticky"
          top={0}
          flex="0 0 auto"
          zIndex={2}
          background="white"
          width="100%"
          maxW="42.5rem"
        >
          <Heading as="h2" fontSize="cartTitle" fontWeight="normal">
            {t('cart-title')}
          </Heading>
          {totalNum > 0 && (
            <Box display="flex" justifyContent="space-between">
              <Text fontSize="sm" color="gray.100">
                {t('cart-resume', { count: totalNum })}
              </Text>
              <Button
                color="primary.500"
                variant="link"
                border="transparent"
                fontSize="sm"
                textDecor="underline"
                backgroundColor="transparent"
                fontWeight="normal"
                onClick={(): void => {
                  setProductToDelete(null)
                  skipIfInPreviewMode(() => {
                    const getCartItemValues = getFlatProductValues(cart)
                    resetCartStart({
                      cart: {
                        size: totalNum,
                        value: Number(totalPrice),
                        ids: getCartItemValues('id'),
                        skus: getCartItemValues('sku'),
                        names: getCartItemValues('name'),
                        categories: getCartItemValues('category'),
                        note: globalOrder.orderNote,
                      },
                      session,
                    })
                  })
                  onOpen()
                }}
              >
                {t('cart-cleanCart')}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
      {/* Body */}
      {totalNum > 0 ? (
        <Box flex="1" height="100%" overflowY="scroll" minH={`${getMinHeight()}px`}>
          {Object.keys(cart).map((key) => (
            <CartItem deleteItem={deleteItem} key={key} product={cart[key]} />
          ))}
        </Box>
      ) : (
        <Box
          flex="1"
          display="flex"
          flexDir="column"
          justifyContent="center"
          alignItems="center"
          h="100%"
          minH={`${getMinHeight()}px`}
          overflowY="scroll"
        >
          <Icon name="settings" size="4.8rem" paddingBottom="0.8rem" />
          <Text fontSize="md" textAlign="center" whiteSpace="pre-line">
            {t('cart-emptyMessage')}
          </Text>
          <Link to={{ pathname: `/${customer}/${order}`, search: location.search }}>
            <Text
              color="primary.500"
              aria-label="backToCart"
              border="transparent"
              fontSize="sm"
              textDecor="underline"
              backgroundColor="transparent"
              fontWeight="normal"
              paddingTop="32px"
            >
              {t('cart-backCatalogue')}
            </Text>
          </Link>
        </Box>
      )}
      {/* Body */}
      {/* Footer */}
      <Box
        paddingY="1.2rem"
        paddingX="0.8rem"
        boxShadow="md"
        position="sticky"
        zIndex={2}
        background="white"
        width="100%"
        maxW="42.5rem"
        bottom={0}
        flex="0 0 auto"
      >
        {totalNum > 0 && config.enabledOrderNote && (
          // TODO agregar un padding
          <AdditionalInfo
            title={t('cart-observations')}
            subtext={t('cart-observationsAlter')}
            value={globalOrder.orderNote}
            onChange={onTextChange}
            inputProps={{ placeholder: t('cart-observationsPlaceholder') }}
          />
        )}
        {!canPlaceOrder && (
          <MinAmountProgress
            currencyPrefix={config.currencyPrefix}
            currentAmount={Number(totalPrice)}
            minAmount={config.minAmount || 0}
          />
        )}
        <Box
          display="flex"
          width="100%"
          paddingY="1.2rem"
          paddingX="1.6rem"
          backgroundColor="primary.500"
          borderColor="transparent"
          fontWeight="500"
          color="white"
          fontFamily="body"
          fontSize="1.1rem"
          borderRadius="0.4rem"
          justifyContent={hidePrices ? 'center' : 'space-between'}
          cursor="pointer"
          onClick={(): boolean | Promise<ExecutionResult<ICreateOrder>> => {
            if (!canPlaceOrder) {
              history.push({ pathname: `/${customer}/${order}`, search: location.search })
              return false
            }

            if (isInPreviewMode(location)) {
              return false
            }

            const getCartItemValues = getFlatProductValues(cart)

            skipIfInPreviewMode(() => {
              orderSent({
                cart: {
                  size: totalNum,
                  value: Number(totalPrice),
                  ids: getCartItemValues('id'),
                  skus: getCartItemValues('sku'),
                  names: getCartItemValues('name'),
                  categories: getCartItemValues('category'),
                  note: globalOrder.orderNote,
                },
                session,
              })
            })

            return createOrder()
          }}
        >
          {canPlaceOrder ? (
            <>
              <Text fontSize="lg" alignSelf="center">
                {t('cart-pay')}
              </Text>
              {!hidePrices && <Text fontSize="lg" alignSelf="center">{`${config.currencyPrefix} ${totalPrice}`}</Text>}
            </>
          ) : (
            <Text fontSize="lg" textAlign="center" flex="1">
              {t('cart-goToCatalogue')}
            </Text>
          )}
        </Box>
      </Box>
      {/* Footer */}
    </Box>
  )
}
